import React, {
    PropsWithChildren,
    useState,
    useEffect,
    KeyboardEventHandler,
    FunctionComponent,
} from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import style from './modal.css'

export type Props = PropsWithChildren<{
    isVisible: boolean
    containerClassName?: string
    layerClassName?: string
    contentClassName?: string
    onClose?: Function
    isFixed?: boolean
}>

const Modal: FunctionComponent<Props> = (props) => {
    const {
        isVisible,
        children,
        containerClassName,
        layerClassName,
        contentClassName,
        onClose,
        isFixed,
    } = props

    useEffect(() => {
        if (!isFixed || !isVisible) {
            return () => undefined
        }

        document.documentElement.classList.add(style.modalOpen)

        return () => {
            document.documentElement.classList.remove(style.modalOpen)
        }
    }, [isFixed, isVisible])

    const [containerElement] = useState<HTMLElement | null>(document.getElementById('dcc-modal'))

    if (!containerElement) {
        console.error('Could not open React.Portal target element')

        return null
    }

    const handleClose = (): void => {
        if (onClose) {
            onClose()
        }
    }

    const onKeyDown: KeyboardEventHandler = (event) => {
        if (event.key === 'Escape') {
            handleClose()
        }
    }

    const content = isVisible ? (
        <motion.div
            className={`${style.container} ${containerClassName}`}
            data-testid="modal-container"
            initial={{
                opacity: 0,
            }}
            animate={{
                opacity: 1,
            }}
            exit={{
                opacity: 0,
            }}
            transition={{
                ease: [0.75, 0.02, 0.5, 1],
                duration: 0.24,
            }}
        >
            <div
                className={`${style.layer} ${layerClassName}`}
                onClick={handleClose}
                role="button"
                tabIndex={0}
                onKeyDown={onKeyDown}
                aria-label="close modal"
                data-testid="modal-layer"
            />
            <div className={`${style.content} ${contentClassName}`} data-testid="modal-content">
                {children}
            </div>
        </motion.div>
    ) : null

    return createPortal(
        <AnimatePresence initial>
            {content}
        </AnimatePresence>,
        containerElement,
    )
}

Modal.defaultProps = {
    containerClassName: '',
    layerClassName: '',
    contentClassName: '',
    onClose: () => undefined,
    isFixed: true,
}

export default Modal
